// ============================================================
//  ADMIN — SPONSOREN & TRÄGERVEREINE
//  Beide Collections haben identische Form (name, logo, url,
//  reihenfolge) — daher eine Seite mit Umschalter statt zwei.
// ============================================================
import { useEffect, useState } from "react";
import { fetchSponsoren, createSponsor, updateSponsor, deleteSponsor } from "../../cms/sponsoren";
import {
  fetchTraegervereine, createTraegerverein, updateTraegerverein, deleteTraegerverein,
} from "../../cms/traegervereine";
import { fotoHochladen } from "../../cms/upload";

const LEER = { name: "", logo: "", url: "", reihenfolge: 0 };

const MODI = {
  sponsoren: { label: "Sponsoren", fetch: fetchSponsoren, create: createSponsor, update: updateSponsor, del: deleteSponsor },
  traegervereine: { label: "Trägervereine", fetch: fetchTraegervereine, create: createTraegerverein, update: updateTraegerverein, del: deleteTraegerverein },
};

export default function Sponsoren() {
  const [modus, setModus] = useState("sponsoren");
  const [liste, setListe] = useState([]);
  const [ladend, setLadend] = useState(true);
  const [formular, setFormular] = useState(null);
  const [hochladen, setHochladen] = useState(false);
  const [speichernLaeuft, setSpeichernLaeuft] = useState(false);
  const [fehler, setFehler] = useState("");

  const aktuell = MODI[modus];

  async function neuLaden() {
    setLadend(true);
    setListe(await aktuell.fetch());
    setLadend(false);
  }
  useEffect(() => { neuLaden(); setFormular(null); }, [modus]);

  function oeffnen(eintrag) {
    setFormular(eintrag ? { ...eintrag } : { ...LEER });
    setFehler("");
  }

  async function logoAuswahl(e) {
    const datei = e.target.files?.[0];
    if (!datei) return;
    setHochladen(true);
    setFehler("");
    try {
      const url = await fotoHochladen(datei, "cms");
      setFormular((f) => ({ ...f, logo: url }));
    } catch (err) {
      setFehler(err.message);
    } finally {
      setHochladen(false);
      e.target.value = "";
    }
  }

  async function speichern(e) {
    e.preventDefault();
    setSpeichernLaeuft(true);
    setFehler("");
    try {
      const daten = { ...formular, reihenfolge: Number(formular.reihenfolge) || 0 };
      if (formular.id) {
        const { id, ...rest } = daten;
        await aktuell.update(id, rest);
      } else {
        await aktuell.create(daten);
      }
      setFormular(null);
      await neuLaden();
    } catch (err) {
      setFehler(err.message);
    } finally {
      setSpeichernLaeuft(false);
    }
  }

  async function loeschen(id) {
    if (!window.confirm("Eintrag wirklich löschen?")) return;
    await aktuell.del(id);
    await neuLaden();
  }

  return (
    <div className="mx-auto max-w-3xl px-4 py-16">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-extrabold uppercase text-primary">Sponsoren & Trägervereine</h1>
      </div>

      <div className="flex gap-2 mb-8">
        {Object.entries(MODI).map(([key, m]) => (
          <button
            key={key}
            onClick={() => setModus(key)}
            className={`px-4 py-2 text-sm font-bold uppercase ${modus === key ? "bg-primary text-white" : "bg-dark/5 text-dark/60 hover:bg-dark/10"}`}
          >
            {m.label}
          </button>
        ))}
        {!formular && (
          <button onClick={() => oeffnen(null)} className="ml-auto bg-primary text-white font-bold uppercase text-sm px-5 py-2.5 hover:bg-accent transition-colors">
            + Neu
          </button>
        )}
      </div>

      {formular && (
        <form onSubmit={speichern} className="border border-dark/10 p-6 mb-10 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-sm font-semibold">Name *</span>
              <input required value={formular.name} onChange={(e) => setFormular({ ...formular, name: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Website-URL</span>
              <input value={formular.url} onChange={(e) => setFormular({ ...formular, url: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
            <label className="block">
              <span className="text-sm font-semibold">Reihenfolge</span>
              <input type="number" value={formular.reihenfolge} onChange={(e) => setFormular({ ...formular, reihenfolge: e.target.value })} className="mt-1 w-full border border-dark/20 px-3 py-2" />
            </label>
          </div>

          <div>
            <span className="text-sm font-semibold">Logo</span>
            {formular.logo && <img src={formular.logo} alt="" className="mt-2 h-16 object-contain bg-dark/5 p-2" />}
            <input type="file" accept="image/*" onChange={logoAuswahl} disabled={hochladen} className="mt-2 text-sm block" />
            {hochladen && <p className="text-sm text-dark/50 mt-1">Lädt hoch …</p>}
          </div>

          {fehler && <p className="text-sm text-red-600">{fehler}</p>}

          <div className="flex gap-3 pt-2">
            <button type="submit" disabled={speichernLaeuft} className="bg-primary text-white font-bold uppercase text-sm px-6 py-2.5 hover:bg-accent transition-colors disabled:opacity-50">
              {speichernLaeuft ? "Speichert …" : "Speichern"}
            </button>
            <button type="button" onClick={() => setFormular(null)} className="font-semibold text-sm text-dark/60 hover:text-primary">
              Abbrechen
            </button>
          </div>
        </form>
      )}

      {ladend ? (
        <p className="text-dark/50">Lädt …</p>
      ) : (
        <ul className="divide-y divide-dark/10">
          {liste.map((s) => (
            <li key={s.id} className="py-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                {s.logo && <img src={s.logo} alt="" className="h-8 w-8 object-contain" />}
                <p className="font-bold">{s.name}</p>
              </div>
              <div className="flex gap-3 shrink-0">
                <button onClick={() => oeffnen(s)} className="text-sm font-semibold text-primary hover:text-accent">Bearbeiten</button>
                <button onClick={() => loeschen(s.id)} className="text-sm font-semibold text-red-600 hover:text-red-800">Löschen</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
